import "./logrocket";
import React from "react";
import ReactDOM from "react-dom";
import BoardService from "./services/board.js";
import firebase from "./firebase";
import Board from "./board/board";
import Filter from "./board/filter";
import { v4 as uuidv4 } from "uuid";
import {
  getCookie,
  setCookie,
  getBoardIdFromURL,
  getCurrentUserId,
  shouldCreateNewBoard,
  shouldRecreateDemoBoard,
  getURLParamValue,
  getVar,
} from "./util/common";
import { templates } from "./util/templates";
import clone from "clone";
import ErrorBoundary from "./ErrorBoundary";
import ErrorService from "./services/error";

/**
 *
 *  Handle Errors for entire application
 *
 */

window.onerror = (message, file, line, col, error) => {
  ErrorService.createErrorObject(error);
  return false;
};
window.addEventListener("error", (e) => {
  ErrorService.createErrorObject(e);
  return false;
});
window.addEventListener("unhandledrejection", (e) => {
  // this handles promises errors
  ErrorService.createErrorObject(e);
});

/**
 *
 * Application Start
 *
 */
const board = firebase.firestore().collection("/boards");

//delete all docs:
// ---> board.get().then((res) => {
//   ---> res.forEach((element) => {
//    --->  element.ref.delete();
//   });
// });

// 1- Set and Get user generated ID from cookies:
let USER_ID = getCookie("user_generated_id");
if (!USER_ID) {
  USER_ID = uuidv4();
  setCookie("user_generated_id", USER_ID, 360);
}

/**** Start of the App here  */

class App extends React.PureComponent {
  constructor(props) {
    super(props);
    this.firebaseSubscribtion = null;
    this.state = {
      newBoardJSON: null,
      originalBoardDBObject: null,
      isValidBoard: null,
      welcomeAnnounced: false,
      // filter fields:
      filters: {
        selectedTags: [],
        showOnlyUserCards: false,
        sortByVotes: false,
      },
    };
  }

  componentDidMount() {
    const { boardID } = this.props;
    if (!boardID) {
      return;
    }
    this.firebaseSubscribtion = board
      .doc(boardID)
      .onSnapshot((boardDbObject) => {
        const boardJSON = boardDbObject.data();
        // if this is first time we're loading the board, verify it's a valid board
        if (this.state.isValidBoard === null) {
          if (!this.isValidBoard(boardJSON)) {
            this.setState({ isValidBoard: false });
          } else {
            this.setState({ isValidBoard: true });
          }
        }
        const originalBoardDBObject = clone(boardJSON);
        const newBoardJSON = this.formatData(boardJSON);
        this.setState({ newBoardJSON, originalBoardDBObject });
      });
  }

  componentWillUnmount() {
    if (typeof this.firebaseSubscribtion === "function") {
      this.firebaseSubscribtion();
    }
  }

  componentDidUpdate() {
    if (
      this.state.isValidBoard === true &&
      !this.state.welcomeAnnounced &&
      this.state.originalBoardDBObject
    ) {
      this.setState({ welcomeAnnounced: true });
      // Land focus on the hidden welcome region so screen readers read it
      // first; arrow-right then walks to the filter/board. Only steal focus
      // if the user hasn't started interacting yet.
      requestAnimationFrame(() => {
        const welcomeEl = document.getElementById("board-welcome");
        if (welcomeEl && document.activeElement === document.body) {
          welcomeEl.focus();
        }
      });
    }
  }

  compare = (a, b) => {
    if (a.index < b.index) {
      return -1;
    }
    if (a.index > b.index) {
      return 1;
    }
    return 0;
  };

  compareByVotes = (a, b) => {
    const calculateTotalVotes = (votes) => {
      return Object.values(votes).reduce((totalVoteNo, votingCategoryArr) => {
        return totalVoteNo + votingCategoryArr.length;
      }, 0);
    };
    const aVotes =
      Object.values(a.votes)?.length > 0 ? calculateTotalVotes(a.votes) : 0;
    const bVotes =
      Object.values(b.votes)?.length > 0 ? calculateTotalVotes(b.votes) : 0;
    if (aVotes > bVotes) {
      return -1;
    }
    if (aVotes < bVotes) {
      return 1;
    }
    return 0;
  };

  formatData = (boardJSON) => {
    const { boardID } = this.props;
    const mapOfAllTags = new Map();
    Object.keys(boardJSON.columns).forEach((columnKey) => {
      // convert cards from map to array
      const columnCardsArray = Object.keys(
        boardJSON.columns[columnKey].cards
      ).map((cardKey) => {
        // step 1: while we're at it, build up map of all tags on the board
        const cardTags =
          boardJSON.columns[columnKey].cards[cardKey]?.tags ?? [];
        if (cardTags) {
          cardTags.forEach((tagObject) =>
            mapOfAllTags.set(tagObject.text, tagObject.color)
          );
        }
        return {
          cardId: cardKey,
          columnId: columnKey,
          boardId: boardID,
          ...boardJSON.columns[columnKey].cards[cardKey],
        };
      });

      if (!columnCardsArray.length) {
        boardJSON.columns[columnKey] = [];
      } else {
        boardJSON.columns[columnKey] = columnCardsArray.sort(this.compare);
      }
      boardJSON.boardPublicId = boardID;
    });
    boardJSON.mapOfAllTags = mapOfAllTags;
    return boardJSON;
  };

  isValidBoard = (boardJSON) => {
    const { boardID } = this.props;
    if (
      boardID &&
      boardID.length > 15 &&
      boardID.length < 25 &&
      boardJSON &&
      boardJSON.id &&
      boardJSON.id?.length > 15 &&
      boardJSON.id?.length < 40 &&
      boardJSON.columns
    ) {
      return true;
    } else {
      return false;
    }
  };

  onFilterChange = (filterFieldName, value) => {
    this.setState({
      filters: {
        ...this.state.filters,
        [filterFieldName]: value,
      },
    });
  };

  isFilterActive = () => {
    const { filters } = this.state;
    const { selectedTags, showOnlyUserCards, sortByVotes } = filters;
    if (
      selectedTags?.length > 0 ||
      showOnlyUserCards === true ||
      sortByVotes === true
    ) {
      return true;
    }
    return false;
  };

  applyFilters = (boardJSON) => {
    const { filters } = this.state;
    let { selectedTags } = filters;
    const { showOnlyUserCards, sortByVotes } = filters;
    selectedTags = selectedTags.map((option) => option.value);
    const copyBoardJSON = clone(boardJSON);
    const findCardIndex = (columnId, cardId) => {
      return copyBoardJSON.columns[columnId]
        .map((cardObject) => cardObject.cardId)
        .indexOf(cardId);
    };
    if (selectedTags && selectedTags.length > 0) {
      Object.keys(boardJSON.columns).forEach((columnKey) => {
        // convert cards from map to array
        boardJSON.columns[columnKey].forEach((card) => {
          // if user selected "show only my cards, then just hide the card and move on"
          if (
            showOnlyUserCards === true &&
            card?.created_by?.length > 0 &&
            card?.created_by !== getCurrentUserId()
          ) {
            const cardIndexToBeDeleted = findCardIndex(columnKey, card.cardId);
            copyBoardJSON.columns[columnKey].splice(cardIndexToBeDeleted, 1);
            return; // i.e: continue
          }
          const cardTags = card?.tags ?? [];
          if (cardTags) {
            const doesCardHasFilteredTags = cardTags.some((tagObject) =>
              selectedTags.includes(tagObject.text)
            );

            if (!doesCardHasFilteredTags) {
              // remove that cards
              const cardIndexToBeDeleted = findCardIndex(
                columnKey,
                card.cardId
              );
              copyBoardJSON.columns[columnKey].splice(cardIndexToBeDeleted, 1);
            }
          } else {
            // if cards doesn't have any tags, and the tags filter has at least one tag, then delete the cards with no tags
            if (selectedTags) {
              const cardIndexToBeDeleted = findCardIndex(
                columnKey,
                card.cardId
              );
              copyBoardJSON.columns[columnKey].splice(cardIndexToBeDeleted, 1);
            }
          }
        });
      });
    } else if (showOnlyUserCards === true) {
      // if no tags were selected, but user selects "only show my cards", then filter cards
      Object.keys(boardJSON.columns).forEach((columnKey) => {
        // convert cards from map to array
        boardJSON.columns[columnKey].forEach((card) => {
          // if user selected "show only my cards, then just hide the card and move on"
          if (
            showOnlyUserCards === true &&
            card?.created_by?.length > 0 &&
            card?.created_by !== getCurrentUserId()
          ) {
            const cardIndexToBeDeleted = findCardIndex(columnKey, card.cardId);
            copyBoardJSON.columns[columnKey].splice(cardIndexToBeDeleted, 1);
            return; // i.e: continue
          }
        });
      });
    }

    if (sortByVotes === true) {
      Object.keys(copyBoardJSON.columns).forEach((columnKey) => {
        if (copyBoardJSON?.columns[columnKey]?.length === 0) {
          // neglict columns with 0 cards
          return;
        }
        const columnCardsArray = copyBoardJSON?.columns[columnKey];
        copyBoardJSON.columns[columnKey] = columnCardsArray.sort(
          this.compareByVotes
        );
      });
    }

    //Handle sorting here
    return copyBoardJSON;
  };

  render() {
    const {
      newBoardJSON,
      isValidBoard,
      originalBoardDBObject,
      filters,
    } = this.state;
    let filteredBoardJSON = newBoardJSON;
    const isFilterActive = this.isFilterActive();
    let numberOfCards = 0;
    if (isValidBoard === false) {
      return (
        <div>Invalid board likn Either ID is wrong format is</div>
      );
    }

    if (isValidBoard === null || newBoardJSON === null) {
      return null;
    }

    if (isFilterActive) {
      filteredBoardJSON = this.applyFilters(newBoardJSON);
    }

    // calculate number of cards
     Object.keys(filteredBoardJSON?.columns).forEach((columnKey) => {
      if (filteredBoardJSON?.columns[columnKey].length){
        numberOfCards += filteredBoardJSON?.columns[columnKey].length;
      }

    });

    const columnNames = originalBoardDBObject?.columns
      ? Object.values(originalBoardDBObject.columns)
          .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
          .map((c) => c.title)
      : [];

    const boardName = originalBoardDBObject?.name || filteredBoardJSON?.name || "this board";
    const columnCount = columnNames.length;
    const welcomeMessage = columnCount > 0
      ? `Welcome to ${boardName} on Swift Board, a collaborative brainstorming tool. Your team has created ${columnCount} ${columnCount === 1 ? "column" : "columns"}: ${columnNames.join(", ")}. Under each column, team members add cards to share their ideas. You can add your own cards to any column, vote on cards your teammates have shared, and reorder cards within a column. Move right to start reading the columns.`
      : `Welcome to ${boardName} on Swift Board, a collaborative brainstorming tool. Your team hasn't created any columns yet. Move right to add a column and start sharing ideas.`;

    return (
      <>
        <ErrorBoundary>
          <Filter
            allTags={newBoardJSON.mapOfAllTags}
            {...filters}
            onFilterChange={this.onFilterChange}
            boardTitle={filteredBoardJSON.name}
            numberOfCards={numberOfCards}
            columnNames={columnNames}
            resetFilters={() =>
              this.setState({
                filters: {
                  selectedTags: [],
                  showOnlyUserCards: false,
                  sortByVotes: false,
                },
              })
            }
          ></Filter>
          <div
            id="board-welcome"
            className="sr-only"
            tabIndex={-1}
            role="region"
            aria-label="Welcome and board overview"
          >
            {welcomeMessage}
          </div>
          <Board
            withScrollableColumns={false}
            lastDBResponseCode={uuidv4()}
            board={filteredBoardJSON}
            DBRepresentationBoardJSON={originalBoardDBObject}
            isFilterActive={isFilterActive}
          />
        </ErrorBoundary>
      </>
    );
  }
}


//2- check if the request is to create new board (i.e url = swiftboard.io/?board=new_board)
if (shouldCreateNewBoard()) {
  const boardName = getURLParamValue("boardName");
  const templateId = getURLParamValue("templateId");
  if (
    templateId &&
    templateId > 0 &&
    templateId < 50 &&
    templates.hasOwnProperty(templateId)
  ) {
    // If there is template provided, use the template to create board
    const template = templates[templateId];
    BoardService.createBoard(boardName, template.columns).then((data) => {
      // update url to add board ID because the app reads URL parameters everywhere
      //window.location.replace(getVar("BASE_URL") + "?board=" + data.id);
      window.location.href = getVar("BASE_URL") + "?board=" + data.id;
      // ReactDOM.render(
      //   <App boardID={data.id} />,
      //   document.getElementById("root")
      // );
    });
  } else {
    // If there is no template prvoided, create empty board
    BoardService.createBoard(boardName).then((data) => {
      // update url to add board ID because the app reads URL parameters everywhere
      // window.location.replace(getVar("BASE_URL") + "?board=" + data.id);
      window.location.href = getVar("BASE_URL") + "?board=" + data.id;
      // ReactDOM.render(
      //   <App boardID={data.id} />,
      //   document.getElementById("root")
      // );
    });
  }
} else {
  // if opening existing board (i.e. url = swiftbaord.io/?board=VP4397sdfjklj)
  ReactDOM.render(
    <App boardID={getBoardIdFromURL()} />,
    document.getElementById("root")
  );
}

// Used to reset demo board if URL has parm "resetDemo"
// You can verify if the demo board data changed in db if you filter documents by id: "604524e8-0b4b-45d2-b4ce-7af4e42654b9"
if (shouldRecreateDemoBoard()) {
  const demoBoardColumnsData = {
    "f2c0735e-155d-40de-9b7e-a750d6040a1c": {
      "created_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
      "title": "To Improve",
      "created_at": {
        "seconds": 1646896643,
        "nanoseconds": 582000000
      },
      "cards": {
        "61149855-5106-4750-ad77-53f9572166a7": {
          "updated_at": {
            "seconds": 1646897123,
            "nanoseconds": 666000000
          },
          "index": 2,
          "votes": {
            "clap": [
              "4647e0d2-37f1-4167-857f-acfdbd1805d5",
              "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc"
            ],
            "awesome": [],
            "like": [
              "4647e0d2-37f1-4167-857f-acfdbd1805d5",
              "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
              "f83dcdea-8b07-4106-852c-615ed9b4c575",
              "5aa8be37-8120-4b00-bbf7-88c079d8cb62"
            ],
            "love": [],
            "insightful": [
              "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc"
            ]
          },
          "created_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
          "created_at": {
            "seconds": 1646897123,
            "nanoseconds": 666000000
          },
          "updated_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
          "tags": [
            {
              "color": "#567DFF",
              "text": "priorities"
            }
          ],
          "content": "Seems like we're facing some bottlenecks in our decision making and get distracted easily as a result"
        },
        "17c93d31-17d5-40ff-9632-bb5200cafb19": {
          "updated_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
          "votes": {
            "clap": [],
            "insightful": [],
            "awesome": [],
            "like": [
              "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
              "4647e0d2-37f1-4167-857f-acfdbd1805d5",
              "5aa8be37-8120-4b00-bbf7-88c079d8cb62"
            ],
            "love": []
          },
          "content": "We're over-committing stories in recent\nsprints, which is causing a lot of\nspillovers and confusing priorities",
          "created_at": {
            "seconds": 1646897059,
            "nanoseconds": 457000000
          },
          "index": 1,
          "updated_at": {
            "seconds": 1646897075,
            "nanoseconds": 362000000
          },
          "created_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
          "tags": [
            {
              "color": "#FFBF56",
              "text": "sprint planning"
            }
          ]
        },
        "6da5e0b4-10be-4176-acc8-1f3911f5e74a": {
          "updated_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
          "created_at": {
            "seconds": 1646896984,
            "nanoseconds": 972000000
          },
          "index": 0,
          "votes": {
            "insightful": [],
            "love": [
              "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc"
            ],
            "awesome": [],
            "like": [
              "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc"
            ],
            "clap": [
              "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc"
            ]
          },
          "tags": [
            {
              "text": "priorities",
              "color": "#567DFF"
            },
            {
              "text": "work capacity",
              "color": "#56A6FF"
            }
          ],
          "created_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
          "content": "Priorities aren't super clear at the\nmoment, which is challenging because\nwe're getting so many requests for\nsupport",
          "updated_at": {
            "seconds": 1646897013,
            "nanoseconds": 628000000
          }
        },
        "03288fbf-c680-4a45-b39f-87efc4890fbc": {
          "tags": [
            {
              "text": "sprint planning",
              "color": "#FFBF56"
            }
          ],
          "created_at": {
            "seconds": 1646897173,
            "nanoseconds": 87000000
          },
          "content": "For every story we pick, it should have clear acceptance criteria and test plans",
          "index": 3,
          "votes": {
            "insightful": [],
            "awesome": [],
            "like": [
              "4647e0d2-37f1-4167-857f-acfdbd1805d5",
              "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
              "f83dcdea-8b07-4106-852c-615ed9b4c575"
            ],
            "clap": [],
            "love": []
          },
          "created_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
          "updated_at": {
            "seconds": 1646897173,
            "nanoseconds": 87000000
          },
          "updated_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc"
        }
      },
      "updated_at": {
        "seconds": 1646896643,
        "nanoseconds": 582000000
      },
      "index": 1,
      "updated_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc"
    },
    "43209252-13f1-45e9-9974-93df4ef1a524": {
      "updated_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
      "updated_at": {
        "seconds": 1646896643,
        "nanoseconds": 582000000
      },
      "created_at": {
        "seconds": 1646896643,
        "nanoseconds": 582000000
      },
      "title": "Action Items",
      "cards": {
        "757b22da-bb21-4080-8e15-cacb2268711a": {
          "created_at": {
            "seconds": 1646897189,
            "nanoseconds": 159000000
          },
          "votes": {
            "insightful": [
              "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc"
            ],
            "awesome": [],
            "love": [],
            "like": [
              "4647e0d2-37f1-4167-857f-acfdbd1805d5"
            ],
            "clap": []
          },
          "index": 0,
          "updated_at": {
            "seconds": 1646897189,
            "nanoseconds": 159000000
          },
          "tags": [
            {
              "color": "#FFBF56",
              "text": "sprint planning"
            },
            {
              "color": "#1aae27",
              "text": "Eric"
            }
          ],
          "content": "Collaborate with PMs and designers to\naccurately estimate our stories",
          "created_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
          "updated_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc"
        },
        "ce66ac9d-87ab-42d9-b7e7-e516f18a4501": {
          "index": 1,
          "created_at": {
            "seconds": 1646897250,
            "nanoseconds": 445000000
          },
          "tags": [
            {
              "color": "#56A6FF",
              "text": "work capacity"
            },
            {
              "color": "#FF5668",
              "text": "Kevin"
            }
          ],
          "created_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
          "updated_at": {
            "seconds": 1646897363,
            "nanoseconds": 763000000
          },
          "updated_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
          "votes": {
            "like": [
              "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
              "f83dcdea-8b07-4106-852c-615ed9b4c575",
              "4647e0d2-37f1-4167-857f-acfdbd1805d5",
              "5aa8be37-8120-4b00-bbf7-88c079d8cb62"
            ],
            "insightful": [
              "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc"
            ],
            "clap": [],
            "awesome": [],
            "love": []
          },
          "content": "Update our Jira to enforce acceptance checks for every story 🤘🏽"
        },
        "0d94dbf1-776b-45d9-82b0-b9a69898d958": {
          "votes": {
            "insightful": [],
            "clap": [],
            "love": [],
            "like": [],
            "awesome": []
          },
          "content": "Work closely with PMs and end-users to set the right expectations and priorities before each quarter planning session",
          "updated_at": {
            "seconds": 1646897299,
            "nanoseconds": 687000000
          },
          "created_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
          "created_at": {
            "seconds": 1646897299,
            "nanoseconds": 687000000
          },
          "index": 2,
          "updated_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
          "tags": [
            {
              "color": "#567DFF",
              "text": "priorities"
            },
            {
              "color": "#5661ff",
              "text": "Josh"
            }
          ]
        }
      },
      "index": 2,
      "created_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc"
    },
    "9a8bfaaf-e7d5-4b71-8c6b-e5a81b6b1a2f": {
      "cards": {
        "30e503d5-27cb-478a-bac1-05f973f57b65": {
          "tags": [
            {
              "color": "#56A6FF",
              "text": "work capacity"
            }
          ],
          "updated_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
          "created_at": {
            "seconds": 1646896870,
            "nanoseconds": 472000000
          },
          "index": 2,
          "content": "We've been having slightly less meetings this quarter which boosted productivity",
          "created_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
          "updated_at": {
            "seconds": 1646897453,
            "nanoseconds": 400000000
          },
          "votes": {
            "clap": [],
            "like": [
              "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
              "f83dcdea-8b07-4106-852c-615ed9b4c575",
              "5aa8be37-8120-4b00-bbf7-88c079d8cb62"
            ],
            "love": [],
            "insightful": [],
            "awesome": []
          }
        },
        "9e979447-6451-43ec-9ecc-0ac3ce9a25ba": {
          "content": "We had a successful collaboration with the pricing team this quarter.\n\nShout-out to Eric for his onboarding\nhelp 🙌",
          "index": 0,
          "updated_at": {
            "seconds": 1646896836,
            "nanoseconds": 1000000
          },
          "created_at": {
            "seconds": 1646896836,
            "nanoseconds": 1000000
          },
          "tags": [],
          "updated_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
          "votes": {
            "awesome": [
              "f83dcdea-8b07-4106-852c-615ed9b4c575"
            ],
            "love": [],
            "clap": [
              "f83dcdea-8b07-4106-852c-615ed9b4c575"
            ],
            "insightful": [],
            "like": [
              "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
              "f83dcdea-8b07-4106-852c-615ed9b4c575",
              "5aa8be37-8120-4b00-bbf7-88c079d8cb62"
            ]
          },
          "created_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc"
        },
        "af54f806-9639-491e-9f6e-10231201b69c": {
          "tags": [],
          "created_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
          "created_at": {
            "seconds": 1646896947,
            "nanoseconds": 542000000
          },
          "updated_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
          "index": 3,
          "updated_at": {
            "seconds": 1646896947,
            "nanoseconds": 542000000
          },
          "content": "LOVED our project kickoff - super\nproductive and energetic way to start a\nproject",
          "votes": {
            "awesome": [
              "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc"
            ],
            "like": [
              "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
              "f83dcdea-8b07-4106-852c-615ed9b4c575"
            ],
            "love": [],
            "insightful": [],
            "clap": [
              "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc"
            ]
          }
        },
        "9acdc154-e600-4823-8791-494c8f3b5fbe": {
          "created_at": {
            "seconds": 1646896921,
            "nanoseconds": 307000000
          },
          "index": 1,
          "created_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
          "updated_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
          "votes": {
            "awesome": [],
            "like": [
              "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
              "f83dcdea-8b07-4106-852c-615ed9b4c575"
            ],
            "clap": [],
            "love": [
              "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc"
            ],
            "insightful": []
          },
          "updated_at": {
            "seconds": 1646896921,
            "nanoseconds": 307000000
          },
          "tags": [
            {
              "text": "WLB",
              "color": "#DB6576"
            }
          ],
          "content": "Really appreciate everyone's respect\ntowards work/life boundaries "
        }
      },
      "title": "Went Well",
      "updated_at": {
        "seconds": 1646896643,
        "nanoseconds": 582000000
      },
      "updated_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
      "index": 0,
      "created_by": "8dbf5dd0-b7ef-4e19-ba56-3dd2fdd8bfbc",
      "created_at": {
        "seconds": 1646896643,
        "nanoseconds": 582000000
      }
    }
  };
 
  BoardService.updateBoard("aLshwIVY6KVIJHBkOah4", demoBoardColumnsData).then((data) => {   
    alert("demo board was reset");
  });
}
